import * as React from 'react';
import { formatPattern } from 'react-router';

import useRouter from 'js/lib/useRouter';

import { useEnableMessagingV2 } from 'bundles/enterprise-admin-hooks/hooks/useFeatures';
import type { SideNavItem, SideNavSubItem } from 'bundles/enterprise-admin-navigation/components/side-nav/base/types';
import {
  type AdminSideNavItemConfig,
  type AdminSideNavRuntimeContext,
  type AdminSideNavSubItemConfig,
  sideNavConfigDefs,
} from 'bundles/enterprise-admin-navigation/components/side-nav/config/sideNavConfigDefs';
import { useCanUseCourseBuilder } from 'bundles/enterprise-admin-navigation/components/side-nav/useCanUseCourseBuilder';
import { computeSearchCarry } from 'bundles/enterprise-admin-navigation/utils/navConfigUtils';
import { useEnterpriseAdminPermissions } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import enterpriseAdminUser from 'bundles/enterprise-admin/enterprise-admin-index';
import type { ThirdPartyOrganization as AdminDashboardOrg } from 'bundles/enterprise-admin/types';

const resolveSubItem = (
  child: AdminSideNavSubItemConfig,
  runtimeContext: AdminSideNavRuntimeContext
): SideNavSubItem | null => {
  if (child.shouldShow && !child.shouldShow(runtimeContext)) return null;
  if (child.requiredPermissions && !runtimeContext.hasPermission(child.requiredPermissions)) return null;

  // Format route template with org slug parameter
  const formattedHref = formatPattern(child.href, runtimeContext.router.params);
  const href = `${formattedHref}${runtimeContext.locationSearchString}`;
  const isActive = child.isActiveOverride
    ? child.isActiveOverride(runtimeContext, formattedHref)
    : runtimeContext.router.isActive(formattedHref);

  return { id: child.id, label: child.getLabel(runtimeContext), href, isActive };
};

/**
 * Walks a single `AdminSideNavItemConfig` through the runtime environment.
 *
 * Applies visibility gates (shouldShow, requiredPermissions) and resolves hrefs,
 * active state, and children. Returns null if the item should be dropped (failed
 * gate or permission check, or parent with all children gated out).
 */
const resolveItem = (item: AdminSideNavItemConfig, runtimeContext: AdminSideNavRuntimeContext): SideNavItem | null => {
  if (item.shouldShow && !item.shouldShow(runtimeContext)) return null;
  if (item.requiredPermissions && !runtimeContext.hasPermission(item.requiredPermissions)) return null;

  const children = item.children
    ?.map((child) => resolveSubItem(child, runtimeContext))
    .filter((c): c is SideNavSubItem => c !== null);

  // Parent with declared but no children to shown is dropped — avoids dead expandables.
  if (item.children && item.children.length > 0 && (!children || children.length === 0)) {
    return null;
  }

  // Format route template with org slug parameter
  const rawHref = item.hrefOverride ? item.hrefOverride(runtimeContext) : item.href;
  const formattedHref = formatPattern(rawHref, runtimeContext.router.params);
  const href = `${formattedHref}${runtimeContext.locationSearchString}`;

  const isActive = item.isActiveOverride
    ? item.isActiveOverride(runtimeContext, formattedHref)
    : runtimeContext.router.isActive(formattedHref);

  return {
    id: item.id,
    label: item.getLabel(runtimeContext),
    icon: item.icon,
    href,
    isActive,
    children,
  };
};

type UseAdminSideNavItemsArgs = {
  thirdPartyOrganization: AdminDashboardOrg;
  enterpriseAdminFeatureFlags: {
    enableSkillsPlanningForOrg: boolean;
    enableNewLearnersPage: boolean;
  };
};

export type AdminSideNavItems = {
  sideNavTopItems: SideNavItem[];
  sideNavBottomItems: SideNavItem[];
};

/**
 * Resolves the declarative side-nav config into ready-to-render items.
 *
 * Splits the result into the main `sideNav` list (rendered by `SideNavBar`'s
 * `items` prop) and the `sideNavBottom` list (rendered inside the `bottom` slot).
 *
 * Action items (e.g. Ask Coursera) are NOT returned here — the consumer
 * component appends them onto `sideNav` itself.
 */
export const useAdminSideNavItems = ({
  thirdPartyOrganization,
  enterpriseAdminFeatureFlags,
}: UseAdminSideNavItemsArgs): AdminSideNavItems => {
  const router = useRouter();
  const { hasPermission, getIsOrganizationDomainAdmin } = useEnterpriseAdminPermissions();
  const { enableSkillsPlanningForOrg, enableNewLearnersPage } = enterpriseAdminFeatureFlags;
  const canUseCourseBuilder = useCanUseCourseBuilder();
  const enableMessagingV2 = useEnableMessagingV2(thirdPartyOrganization.id);

  const isSuperUserOrEnterpriseOutsourcing = enterpriseAdminUser.isSuperUserOrEnterpriseOutsourcing();
  const isFreemiumOrgAndCanOnlyManagePrograms =
    enterpriseAdminUser.isFreemiumOrg && enterpriseAdminUser.canOnlyManagePrograms();

  const locationSearchString = React.useMemo(
    () => computeSearchCarry(router.location.search || ''),
    [router.location.search]
  );

  // `router` is replaced on every navigation by the router context provider, so the
  // memo recomputes — fresh isActive flags on every URL change without an extra dep.
  return React.useMemo(() => {
    const runtime: AdminSideNavRuntimeContext = {
      router,
      thirdPartyOrganization,
      enableSkillsPlanningForOrg,
      enableNewLearnersPage,
      enableMessagingV2,
      hasPermission,
      isOrganizationDomainAdmin: getIsOrganizationDomainAdmin(),
      canUseCourseBuilder,
      isSuperUserOrEnterpriseOutsourcing,
      isFreemiumOrgAndCanOnlyManagePrograms,
      locationSearchString,
    };

    const sideNavTopItems: SideNavItem[] = [];
    const sideNavBottomItems: SideNavItem[] = [];

    sideNavConfigDefs.forEach((item) => {
      const resolved = resolveItem(item, runtime);
      if (!resolved) return;
      if (item.slot === 'side-nav-bottom') {
        sideNavBottomItems.push(resolved);
      } else {
        sideNavTopItems.push(resolved);
      }
    });

    return { sideNavTopItems, sideNavBottomItems };
  }, [
    router,
    thirdPartyOrganization,
    enableSkillsPlanningForOrg,
    enableNewLearnersPage,
    enableMessagingV2,
    hasPermission,
    getIsOrganizationDomainAdmin,
    canUseCourseBuilder,
    isSuperUserOrEnterpriseOutsourcing,
    isFreemiumOrgAndCanOnlyManagePrograms,
    locationSearchString,
  ]);
};
